import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import { UserProfileProvider, useUserProfile } from './context/UserProfileContext';
import { BannerProvider } from './context/BannerContext';
import { VersionProvider } from './context/VersionContext';
import { ThemeProvider } from './context/ThemeContext';
import { AppShell } from './components/layout/AppShell';
import { MoveShell } from './components/layout/MoveShell';
import { TermsGate } from './components/TermsGate';
import { AuthPage, isPendingSignup } from './pages/AuthPage';
import { TermsPage } from './pages/TermsPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { QuoteHistoryPage } from './pages/quotes/QuoteHistoryPage';
import { SalesPage } from './pages/SalesPage';
import { DatesLogisticsPage } from './pages/quotes/DatesLogisticsPage';
import { CasegoodsInventoryPage } from './pages/quotes/CasegoodsInventoryPage';
import { ResidentialInventoryPage } from './pages/quotes/ResidentialInventoryPage';
import { ModularWorkstationsPage } from './pages/quotes/ModularWorkstationsPage';
import { ScopeOfServicesPage } from './pages/quotes/ScopeOfServicesPage';
import { AccessorialServicesPage } from './pages/quotes/AccessorialServicesPage';
import { MaterialsPage } from './pages/quotes/MaterialsPage';
import { FormulaDetailsPage } from './pages/quotes/FormulaDetailsPage';
import { QuoteBreakdownPage } from './pages/quotes/QuoteBreakdownPage';
import { ClientQuotesPage } from './pages/quotes/ClientQuotesPage';
import { QuoteVersionViewPage } from './pages/quotes/QuoteVersionViewPage';
import { PhotosNotesPage } from './pages/quotes/PhotosNotesPage';
import { IomiAdminToolsPage } from './pages/admin/IomiAdminToolsPage';
import { IomiDashboardPage } from './pages/admin/IomiDashboardPage';
import { ManageUsersPage } from './pages/admin/ManageUsersPage';
import { ManageOrganizationsPage } from './pages/admin/ManageOrganizationsPage';
import { CreateOrganizationPage } from './pages/admin/CreateOrganizationPage';
import { OrgDetailsPage } from './pages/admin/OrgDetailsPage';
import { OrgUsersPage } from './pages/admin/OrgUsersPage';
import { MasterInventoryPage } from './pages/admin/MasterInventoryPage';
import { MasterServicesPage } from './pages/admin/MasterServicesPage';
import { ManagePricingPage } from './pages/admin/ManagePricingPage';
import { DeveloperToolsPage } from './pages/admin/DeveloperToolsPage';
import { AppVersionsPage } from './pages/admin/AppVersionsPage';
import { MasterResidentialDefaultsPage } from './pages/admin/MasterResidentialDefaultsPage';
import { FleetDefaultsPage } from './pages/admin/FleetDefaultsPage';
import { InvitationWelcomePage } from './pages/InvitationWelcomePage';
import { NotInvitedPage } from './pages/NotInvitedPage';
import { DashboardPage } from './pages/DashboardPage';
import { MyProfilePage } from './pages/MyProfilePage';
import { NotificationPreferencesPage } from './pages/NotificationPreferencesPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { AuthActionPage } from './pages/AuthActionPage';
import { ChoosePlanPage } from './pages/ChoosePlanPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { CheckoutSuccessPage } from './pages/CheckoutSuccessPage';
import { MoverJobsPage } from './pages/MoverJobsPage';
import { LeadsListPage } from './pages/leads/LeadsListPage';
import { MoveIndexPage } from './pages/unified/MoveIndexPage';
import { MovePaymentsPage } from './pages/unified/MovePaymentsPage';
import { MoveCrewTrucksPage } from './pages/unified/MoveCrewTrucksPage';
import { MoveAccessLogisticsPage } from './pages/unified/MoveAccessLogisticsPage';
import { MoveAccessPropertyPage } from './pages/unified/MoveAccessPropertyPage';
import { MoveActivityPage } from './pages/unified/MoveActivityPage';
import { CustomerLeadDetailsPage } from './pages/unified/CustomerLeadDetailsPage';
import { DispatchPage } from './pages/DispatchPage';
import { FleetPage } from './pages/FleetPage';
import { OperationsPage } from './pages/OperationsPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { WhatsNewPage } from './pages/WhatsNewPage';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import { CustomerPaymentPage } from './pages/public/CustomerPaymentPage';
import { HomeDashboardPage } from './pages/HomeDashboardPage';
import { SetupWizardPage } from './pages/setup/SetupWizardPage';
import { NewAdminPanelPage } from './pages/admin/NewAdminPanelPage';
import { CompanyHubPage } from './pages/admin/CompanyHubPage';
import { CompanyProfilePage } from './pages/admin/CompanyProfilePage';
import { QuotingHubPage } from './pages/admin/QuotingHubPage';
import { QuoteDefaultsPage } from './pages/admin/QuoteDefaultsPage';
import { PaymentsHubPage } from './pages/admin/PaymentsHubPage';
import { CustomerFacingHubPage } from './pages/admin/CustomerFacingHubPage';
import { OperationsHubPage } from './pages/admin/OperationsHubPage';
import { PlatformHubPage } from './pages/admin/PlatformHubPage';
import { IomiOrgsUsersHubPage } from './pages/admin/IomiOrgsUsersHubPage';
import { IomiBillingHubPage } from './pages/admin/IomiBillingHubPage';
import { TaxSettingsPage } from './pages/admin/TaxSettingsPage';
import { IomiDefaultsHubPage } from './pages/admin/IomiDefaultsHubPage';
import { PlatformQuoteDefaultsPage } from './pages/admin/PlatformQuoteDefaultsPage';
import { IomiSystemHubPage } from './pages/admin/IomiSystemHubPage';
import {
  CompanyUsersPage, CompanyBillingPage, CompanyBillingPlanPage, CompanyBillingPaymentsPage,
  QuotingRateCardsPage, QuotingResidentialRoomsPage, QuotingInventoryItemsPage, QuotingServicesPage,
  PaymentsMethodsPage, PaymentsSchedulePage,
  CustomerFacingBookingFormPage, CustomerFacingOnlineQuotesPage,
  OperationsManageFleetPage, OperationsManageWarehousesPage,
  PlatformFeaturesPage, PlatformIntegrationsPage,
} from './pages/admin/NewAdminRoutes';

// ── Legacy route redirects ───────────────────────────────────────────────────
// Old /leads/:id, /quotes/:id, /jobs/:id routes now redirect to /moves/:id.

function LeadRedirect() {
  const { quoteId } = useParams<{ quoteId: string }>();
  return <Navigate to={`/moves/${quoteId}`} replace />;
}

function QuoteRedirect() {
  const { quoteId, '*': rest } = useParams<{ quoteId: string; '*': string }>();
  return <Navigate to={`/moves/${quoteId}${rest ? `/${rest}` : ''}`} replace />;
}

function JobRedirect() {
  const { quoteId } = useParams<{ quoteId: string }>();
  return <Navigate to={`/moves/${quoteId}`} replace />;
}

/** Renders after the user is authenticated and UserProfileContext is ready. */
function AuthenticatedRoutes() {
  const {
    loading, newlyAccepted, memberships, currentUser, clearNewlyAccepted,
    hasActiveSubscription, subscriptionSkipped, adminOrg, activeOrg,
    needsTermsAcceptance, markTermsAccepted,
  } = useUserProfile();
  const { signOut } = useAuth();

  const location = useLocation();

  const isPlatformAdminOnly = currentUser?.isPlatformAdmin && memberships.length === 0;
  // Show My Moves as home if org has dispatch, jobs, or lead pipeline; otherwise fall back to Sales (Quotes)
  const hasMovesFeatures = activeOrg?.featureDispatch !== false
    || activeOrg?.featureJobs !== false
    || (activeOrg?.featureLeadPipeline ?? false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Just accepted one or more invitations — show welcome screen before the app.
  if (newlyAccepted.length > 0) {
    return <InvitationWelcomePage memberships={newlyAccepted} onContinue={clearNewlyAccepted} />;
  }

  // Authenticated but not part of any org — block access unless the user is a
  // platform admin (who can operate without an org membership).
  if (memberships.length === 0 && !currentUser?.isPlatformAdmin) {
    return <NotInvitedPage />;
  }

  // Org admin without an active subscription who hasn't skipped — show Choose Plan.
  // Allow /checkout and /checkout/success through so the subscription flow can complete.
  const isCheckoutRoute = location.pathname.startsWith('/checkout');
  if (adminOrg && !hasActiveSubscription && !subscriptionSkipped && !currentUser?.isPlatformAdmin && !isCheckoutRoute) {
    return <ChoosePlanPage />;
  }

  // T&C re-acknowledgment gate — block until user accepts the current terms version.
  if (needsTermsAcceptance) {
    return <TermsGate onAccepted={markTermsAccepted} onSignOut={signOut} />;
  }

  return (
    <>
      <Routes>
        {/* Full-screen pages — no AppShell nav */}
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
        <Route path="/setup" element={<SetupWizardPage />} />

        <Route element={<AppShell />}>
          {/* Home page: Platform admins without org see Admin Tools,
              orgs with dispatch/jobs/leads see My Moves, otherwise Sales (Quotes) */}
          <Route path="/" element={isPlatformAdminOnly ? <IomiAdminToolsPage /> : hasMovesFeatures ? <HomeDashboardPage /> : <SalesPage />} />
          <Route path="/sales" element={<SalesPage />} />
          <Route path="/leads" element={<LeadsListPage />} />
          <Route path="/leads/:quoteId" element={<LeadRedirect />} />
          <Route path="/operations" element={<OperationsPage />} />
          <Route path="/dispatch" element={<DispatchPage />} />
          <Route path="/fleet" element={<FleetPage />} />
          {/* Jobs routes — accessible to all authenticated users */}
          <Route path="/jobs" element={<MoverJobsPage />} />
          <Route path="/jobs/:quoteId" element={<JobRedirect />} />
          {/* Move page — /moves/:id uses MoveShell for persistent
              sticky left panel (hero + nav) on md+ with sub-pages via <Outlet />. */}
          <Route path="/moves/:quoteId" element={<MoveShell />}>
            <Route index element={<MoveIndexPage />} />
            <Route path="customer-lead" element={<CustomerLeadDetailsPage />} />
            <Route path="communications" element={<MoveActivityPage />} />
            <Route path="history" element={<QuoteHistoryPage />} />
            <Route path="activity" element={<QuoteHistoryPage />} />{/* backward compat */}
            <Route path="breakdown" element={<QuoteBreakdownPage />} />
            <Route path="client-quotes" element={<ClientQuotesPage />} />
            <Route path="client-quotes/:versionId" element={<QuoteVersionViewPage />} />
            <Route path="payments" element={<MovePaymentsPage />} />
            <Route path="crew-trucks" element={<MoveCrewTrucksPage />} />
            <Route path="dates-logistics" element={<DatesLogisticsPage />} />
            <Route path="access-logistics" element={<MoveAccessLogisticsPage />} />
            <Route path="access-property" element={<MoveAccessPropertyPage />} />
            <Route path="scope-of-services" element={<ScopeOfServicesPage />} />
            <Route path="residential-rooms" element={<ResidentialInventoryPage />} />
            <Route path="casegoods" element={<CasegoodsInventoryPage />} />
            <Route path="modular-workstations" element={<ModularWorkstationsPage />} />
            <Route path="materials" element={<MaterialsPage />} />
            <Route path="accessorial-services" element={<AccessorialServicesPage />} />
            <Route path="photos-notes" element={<PhotosNotesPage />} />
            <Route path="formula" element={<FormulaDetailsPage />} />
          </Route>
          {/* Legacy /quotes/:id routes → redirect to /moves/:id */}
          <Route path="/quotes/:quoteId/*" element={<QuoteRedirect />} />
          <Route path="/dashboard" element={hasMovesFeatures ? <DashboardPage /> : <Navigate to="/" replace />} />
          {/* ── Admin Panel (hub-based) ──────────────────────────── */}
          <Route path="/admin" element={<NewAdminPanelPage />} />
          {/* Platform super-admin tools */}
          <Route path="/admin/platform-tools" element={<IomiAdminToolsPage />} />
          <Route path="/admin/platform-tools/orgs-users" element={<IomiOrgsUsersHubPage />} />
          <Route path="/admin/platform-tools/billing" element={<IomiBillingHubPage />} />
          <Route path="/admin/platform-tools/defaults" element={<IomiDefaultsHubPage />} />
          <Route path="/admin/platform-tools/system" element={<IomiSystemHubPage />} />
          <Route path="/admin/platform-tools/dashboard" element={<IomiDashboardPage />} />
          <Route path="/admin/platform-tools/users" element={<ManageUsersPage />} />
          <Route path="/admin/platform-tools/organizations" element={<ManageOrganizationsPage />} />
          <Route path="/admin/platform-tools/organizations/new" element={<CreateOrganizationPage />} />
          <Route path="/admin/platform-tools/organizations/:orgId" element={<OrgDetailsPage />} />
          <Route path="/admin/platform-tools/organizations/:orgId/users" element={<OrgUsersPage />} />
          <Route path="/admin/platform-tools/master-inventory" element={<MasterInventoryPage />} />
          <Route path="/admin/platform-tools/master-services" element={<MasterServicesPage />} />
          <Route path="/admin/platform-tools/pricing" element={<ManagePricingPage />} />
          <Route path="/admin/platform-tools/tax-settings" element={<TaxSettingsPage />} />
          <Route path="/admin/platform-tools/developer-tools" element={<DeveloperToolsPage />} />
          <Route path="/admin/platform-tools/master-residential-defaults" element={<MasterResidentialDefaultsPage />} />
          <Route path="/admin/platform-tools/platform-quote-defaults" element={<PlatformQuoteDefaultsPage />} />
          <Route path="/admin/platform-tools/app-versions" element={<AppVersionsPage />} />
          <Route path="/admin/platform-tools/fleet-defaults" element={<FleetDefaultsPage />} />
          {/* Company hub */}
          <Route path="/admin/company" element={<CompanyHubPage />} />
          <Route path="/admin/company/profile" element={<CompanyProfilePage />} />
          <Route path="/admin/company/users" element={<CompanyUsersPage />} />
          <Route path="/admin/company/billing" element={<CompanyBillingPage />} />
          <Route path="/admin/company/billing/plan" element={<CompanyBillingPlanPage />} />
          <Route path="/admin/company/billing/payments" element={<CompanyBillingPaymentsPage />} />
          {/* Quoting hub */}
          <Route path="/admin/quoting" element={<QuotingHubPage />} />
          <Route path="/admin/quoting/quote-defaults" element={<QuoteDefaultsPage />} />
          <Route path="/admin/quoting/rate-cards" element={<QuotingRateCardsPage />} />
          <Route path="/admin/quoting/residential-rooms" element={<QuotingResidentialRoomsPage />} />
          <Route path="/admin/quoting/inventory-items" element={<QuotingInventoryItemsPage />} />
          <Route path="/admin/quoting/services" element={<QuotingServicesPage />} />
          {/* Payments hub */}
          <Route path="/admin/payments" element={<PaymentsHubPage />} />
          <Route path="/admin/payments/payment-methods" element={<PaymentsMethodsPage />} />
          <Route path="/admin/payments/payment-schedule" element={<PaymentsSchedulePage />} />
          {/* Customer-Facing hub */}
          <Route path="/admin/customer-facing" element={<CustomerFacingHubPage />} />
          <Route path="/admin/customer-facing/booking-form" element={<CustomerFacingBookingFormPage />} />
          <Route path="/admin/customer-facing/online-quotes" element={<CustomerFacingOnlineQuotesPage />} />
          {/* Operations hub */}
          <Route path="/admin/operations" element={<OperationsHubPage />} />
          <Route path="/admin/operations/manage-fleet" element={<OperationsManageFleetPage />} />
          <Route path="/admin/operations/manage-warehouses" element={<OperationsManageWarehousesPage />} />
          {/* Platform hub */}
          <Route path="/admin/platform" element={<PlatformHubPage />} />
          <Route path="/admin/platform/features" element={<PlatformFeaturesPage />} />
          <Route path="/admin/platform/integrations" element={<PlatformIntegrationsPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/whats-new" element={<WhatsNewPage />} />
          <Route path="/profile" element={<MyProfilePage />} />
          <Route path="/profile/notification-preferences" element={<NotificationPreferencesPage />} />
          {/* Catch-all: unknown or stale URLs (e.g. /login after signing in) go home
              instead of rendering nothing. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>

    </>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Show public routes when not authenticated OR during the brief signup
  // create-then-sign-out window (prevents a flash of the authenticated UI).
  if (!user || isPendingSignup()) {
    return (
      <Routes>
        <Route path="/auth/action" element={<AuthActionPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/pay/:orgId/:quoteId" element={<CustomerPaymentPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="*" element={<AuthPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Firebase auth actions — accessible regardless of auth state */}
      <Route path="/auth/action" element={<AuthActionPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/pay/:orgId/:quoteId" element={<CustomerPaymentPage />} />
      <Route path="/terms" element={<TermsPage />} />

      {/* Everything else requires auth */}
      <Route
        path="/*"
        element={
          <UserProfileProvider>
            <BannerProvider>
              <VersionProvider>
                <AuthenticatedRoutes />
              </VersionProvider>
            </BannerProvider>
          </UserProfileProvider>
        }
      />
    </Routes>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,       // 30s — data is fresh for 30s before a background refetch
      refetchOnWindowFocus: true, // Refresh when user tabs back to the app
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
